<template>
  <AuthUser ref="refAuthUser" @on-success="handleShowConfirm"></AuthUser>
</template>

<script setup>
import { deleteAccount, getPlanList } from '@/api/user/user'
import gtagTools from '@/libs/gtag-tools'
import { logOutSuccess } from '@/services/login'
import { ElMessage, ElMessageBox } from 'element-plus'
import { getCurrentInstance, ref } from 'vue'
import AuthUser from './auth-user.vue'
import { userStore } from '@/stores'

const { $t } = getCurrentInstance().proxy

const props = defineProps({
  // 是否是企业管理员，如果是则提醒需要先解散企业才能删除账号
  isCompanyAdmin: {
    type: Boolean,
    default: false,
  }
})

// 弹出验证身份弹窗
const refAuthUser = ref(null)
const planList = []

const showUserAuth = async () => {
  const companyData = userStore.userInfo.companyInfo
  // 如果是企业管理元，提示先解散企业，再去管理后台删除账号
  if (props.isCompanyAdmin) {
    ElMessageBox.alert(
      $t('The current account is the administrator of {companyName}, please log in to the enterprise management platform and dissolve the company to complete the deletion.', { companyName: companyData.companyName }),
      {
        showConfirmButton: true,
        confirmButtonClass: 'el-button--danger',
        center: true
      }
    )
    return false
  }
  // 企业员工删除账号，可以直接删除
  if (userStore.companyUser) {
    handleShowConfirm()
    return
  }
  const { data } = await getPlanList()
  planList.splice(0, planList.length, ...data)
  // 检测是否有订阅中的plan，如果有，则提示先取消订阅，才能删除账号
  const hasSubscriptionPlan = planList.filter(plan => plan.nextPayTime > 0).length > 0
  if (hasSubscriptionPlan) {
    ElMessageBox.alert(
      $t('http_error_2204'),
      {
        showConfirmButton: true,
        center: true,
      })
    return false
  }
  refAuthUser.value.open()
}

/**
 * 确认删除账号
 * @param action 
 * @param instance 
 * @param done 
 */
const handleDeleteAccount = async (action, instance, done) => {
  if (action === 'confirm') {
    const planType = planList[0]?.planType
    instance.confirmButtonLoading = true
    try {
      await deleteAccount()
      logOutSuccess(false)
      done()
      gtagTools.deleteAccount(planType, true)
    } catch (e) {
      gtagTools.deleteAccount(planType, false)
    }
    instance.confirmButtonLoading = false
  } else {
    done()
  }
}

/**
 * 弹出删除账号弹窗
 * 如果有验证身份，则直接提示后删除账号
 * 否则需要输入uid后才能删除账号
 * @param authType 删除账号前的验证方式，如果为空说明没有绑定账号，比如企业个人账号
 */
const handleShowConfirm = async (authType = '') => {
  try {
    if (authType) {
      ElMessageBox.confirm(
        $t('Permanently delete your personal information and all data, this operation can’t be reconvered.'),
        $t('Confirm'),
        {
          center: true,
          confirmButtonText: $t('Delete'),
          cancelButtonText: $t('Cancel'),
          confirmButtonClass: 'el-button--danger',
          type: 'none',
          beforeClose: async (action, instance, done) => {
            handleDeleteAccount(action, instance, done)
          },
        }
      )
    } else {
      ElMessageBox.prompt(
        $t('Permanently delete your personal information and all data, this operation can\'t be recovered. Enter <span style="color: var(--el-color-primary)">{uid}</span> to confirm the operation.', { uid: userStore.uid }),
        $t('Confirm'),
        {
          confirmButtonText: $t('Delete'),
          confirmButtonClass: 'el-button--danger',
          cancelButtonText: $t('Cancel'),
          center: true,
          inputValidator: (value) => {
            if (!value) {
              return $t('')
            } else if (parseInt(value) === userStore.uid) {
              return true
            } else {
              return false
            }
          },
          inputErrorMessage: $t('Confirm input error'),
          dangerouslyUseHTMLString: true,
          type: 'none',
          beforeClose: async (action, instance, done) => {
            handleDeleteAccount(action, instance, done)
          },
        }
      )
    }
  } catch (e) {
    ElMessage.error(e.message)
  }
}

defineExpose({
  showUserAuth
})
</script>